import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, } from 'react';
import { toast, ToastContainer, } from 'react-toastify/unstyled';
import { api, } from './../api/Api.js';
const FRONTEND_UPDATE_TOAST_ID = 'frontend-update-toast';
function isAbortError(error) {
    return error instanceof DOMException && error.name === 'AbortError';
}
/**
 * Returns whether the current browsing context was loaded by a reload.
 *
 * This is intentionally based on the current window's Performance API.
 * Every iframe has its own Window and therefore its own navigation entry.
 *
 * That means:
 *
 * - initial iframe load -> "navigate"
 * - automatic reload of that iframe -> "reload"
 *
 * So every iframe can independently perform one automatic reload without
 * causing an infinite reload loop.
 */
function wasLoadedByReload() {
    const navigationEntries = performance.getEntriesByType('navigation');
    const navigationEntry = navigationEntries[0];
    return navigationEntry?.type === 'reload';
}
export function useFrontendUpdateCheck({ intervalMs = 10_000, checkForUpdate, onUpdateRequired, }) {
    const checkForUpdateRef = useRef(checkForUpdate);
    const onUpdateRequiredRef = useRef(onUpdateRequired);
    const firstCheckRef = useRef(true);
    useEffect(function updateRefsEffect() {
        checkForUpdateRef.current = checkForUpdate;
        onUpdateRequiredRef.current = onUpdateRequired;
    }, [checkForUpdate, onUpdateRequired]);
    useEffect(function pollingEffect() {
        let isDisposed = false;
        let isStopped = false;
        let timeoutId = null;
        let abortController = null;
        function scheduleNext() {
            if (isDisposed || isStopped) {
                return;
            }
            timeoutId = setTimeout(run, intervalMs);
        }
        async function run() {
            if (isDisposed || isStopped) {
                return;
            }
            abortController = new AbortController();
            try {
                const result = await checkForUpdateRef.current({
                    signal: abortController.signal,
                });
                if (isDisposed || isStopped || abortController.signal.aborted) {
                    return;
                }
                if (result.shouldRefresh) {
                    /**
                     * Automatically reload only when:
                     *
                     * 1. this is the first successful version check for this
                     *    application instance, and
                     * 2. this browsing context was not itself loaded by a
                     *    reload.
                     *
                     * After reload:
                     *
                     *   wasLoadedByReload() === true
                     *
                     * Therefore, if the browser/server still gives us the old
                     * frontend, we show the update toast instead of entering
                     * an infinite reload loop.
                     *
                     * Each iframe has its own navigation entry, so sibling
                     * iframes do not interfere with each other.
                     */
                    if (firstCheckRef.current
                        && !wasLoadedByReload()) {
                        reloadApplication();
                        return;
                    }
                    isStopped = true;
                    onUpdateRequiredRef.current(result);
                    return;
                }
                firstCheckRef.current = false;
            }
            catch (error) {
                if (isAbortError(error)) {
                    return;
                }
                console.error('Frontend update check failed:', error);
            }
            scheduleNext();
        }
        void run();
        function cleanup() {
            isDisposed = true;
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
            }
            if (abortController !== null) {
                abortController.abort();
            }
        }
        return cleanup;
    }, [intervalMs]);
}
function reloadApplication() {
    // @ts-expect-error force reload from server, bypassing cache
    window.location.reload(true);
}
function FrontendUpdateToastContent() {
    return (_jsxs("div", { className: "\n\t\t\tflex\n\t\t\tmax-w-full\n\t\t\tflex-col\n\t\t\trounded-sm\n\t\t\tbg-warning\n\t\t\ttext-white\n\t\t\tshadow-[0_3px_12px_rgba(0,0,0,0.22)]\n\t\t\tsm:flex-row\n\t\t\tsm:items-center\n\t\t", children: [_jsx("div", { className: "min-w-0 px-4 pb-2 pt-3 sm:flex-1 sm:py-3", children: _jsx("div", { className: "min-w-0 text-base leading-6", children: "\u26A0\uFE0F Neue Version ist verf\u00FCgbar. Bitte aktualisieren Sie die Seite." }) }), _jsx("div", { className: "px-4 pb-3 pt-0 sm:shrink-0 sm:py-3", children: _jsx("button", { type: "button", onClick: reloadApplication, className: "\n\t\t\t\t\t\ttext-base\n\t\t\t\t\t\tfont-medium\n\t\t\t\t\t\tleading-6\n\t\t\t\t\t\ttext-white\n\t\t\t\t\t\ttransition-opacity\n\t\t\t\t\t\thover:opacity-80\n\t\t\t\t\t\tfocus-visible:outline-none\n\t\t\t\t\t\tfocus-visible:ring-2\n\t\t\t\t\t\tfocus-visible:ring-white\n\t\t\t\t\t\tfocus-visible:ring-offset-2\n\t\t\t\t\t\tfocus-visible:ring-offset-warning\n\t\t\t\t\t", children: "Jetzt aktualisieren" }) })] }));
}
function showFrontendUpdateToast() {
    if (toast.isActive(FRONTEND_UPDATE_TOAST_ID)) {
        return;
    }
    const options = {
        toastId: FRONTEND_UPDATE_TOAST_ID,
        autoClose: false,
        closeButton: false,
        closeOnClick: false,
        draggable: false,
        hideProgressBar: true,
        icon: false,
    };
    toast(_jsx(FrontendUpdateToastContent, {}), options);
}
export function FrontendUpdateToastContainer() {
    useFrontendUpdateCheck({
        async checkForUpdate({ signal }) {
            const result = await api.common.version.request({
                version: process.env.RELEASE_VERSION,
            }, signal);
            return {
                shouldRefresh: result.status === 'ok' ? result.refresh : false,
            };
        },
        onUpdateRequired: showFrontendUpdateToast,
    });
    return (_jsx(ToastContainer, { position: "top-center", autoClose: false, closeButton: false, hideProgressBar: true, newestOnTop: true, closeOnClick: false, draggable: false, pauseOnHover: false, pauseOnFocusLoss: false, limit: 1, className: "\n\t\t\t\tpointer-events-none\n\t\t\t\tfixed!\n\t\t\t\tleft-1/2!\n\t\t\t\ttop-5!\n\t\t\t\tz-9999!\n\t\t\t\tm-0!\n\t\t\t\tw-auto!\n\t\t\t\tmax-w-[calc(100vw-32px)]!\n\t\t\t\t-translate-x-1/2!\n\t\t\t\tp-0!\n\t\t\t", toastClassName: "\n\t\t\t\tpointer-events-auto\n\t\t\t\tm-0!\n\t\t\t\tw-auto!\n\t\t\t\tmax-w-[calc(100vw-32px)]!\n\t\t\t\tmin-h-0!\n\t\t\t\tbg-transparent!\n\t\t\t\tp-0!\n\t\t\t\tshadow-none!\n\t\t\t" }));
}
//# sourceMappingURL=FrontendUpdateToastContainer.js.map